import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { FOHLayout } from '../components/Layout';
import { authGate, FOHLoading } from './guards';
import { useAuth } from '@verlo/shared/contexts/AuthContext';

// FOH pages — every one of these talks to a router `server_node.py` mounts.
import TablesPage from '../pages/TablesPage';
import TableOrderPage from '../pages/TableOrderPage';
import BarOrdersPage from '../pages/BarOrdersPage';
import OrdersPage from '../pages/OrdersPage';
import PaymentSuccessPage from '../pages/PaymentSuccessPage';
import KitchenDisplayPage from '../pages/KitchenDisplayPage';
import ReservationsPage from '../pages/ReservationsPage';
import KioskLoginPage from '../pages/KioskLoginPage';
import ServicePage from '../pages/ServicePage';
import TillPage, { ZReadPage } from '../pages/TillPage';

// `/reservations?walkin=1` and the rest: the same page at its own name, query kept.
function ToBookings() {
  const { search } = useLocation();
  return <Navigate to={`/bookings${search}`} replace />;
}

export const FOHRoute = ({ children }) => {
  const gate = authGate(useAuth(), FOHLoading);
  return gate || <FOHLayout>{children}</FOHLayout>;
};

// One order page per table or order. Moving a party changes the URL and not the route, and
// a page that stayed mounted kept the old table's cart, then dropped its unsent items when it
// loaded the new table, and never read the new table's draft.
const TableOrderAt = () => {
  const { tableId, orderId } = useParams();
  return <TableOrderPage key={orderId || tableId} />;
};

/**
 * The Node's router tree: the FOH app, plus the two public pages whose backends are
 * node-owned — the PIN kiosk (`pin_auth:node_router`) and the payment return (`payments`).
 *
 * Guest QR ordering is **not** here: since S4-8 it is cloud-served (guests order on their own
 * 4G, `qr_ordering` mounts on cloud-api), so `QROrderPage` lives on `cloudRoutes`.
 *
 * Nothing here is lazy. A kiosk that has lost its WAN must not discover that a route it
 * needs lives in a chunk it never fetched; the whole point of the Node is that it works
 * from the Pi's own disk.
 */
export default function nodeRoutes() {
  return (
    <>
      {/* Public */}
      <Route path="/kiosk" element={<KioskLoginPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />

      {/* Front of house */}
      <Route path="/tables" element={<FOHRoute><TablesPage /></FOHRoute>} />
      <Route path="/tables/:tableId/order" element={<FOHRoute><TableOrderAt /></FOHRoute>} />
      <Route path="/bookings" element={<FOHRoute><ReservationsPage /></FOHRoute>} />
      {/* The nav has always said Bookings (Chris's review M13); the old address still works. */}
      <Route path="/reservations" element={<ToBookings />} />
      <Route path="/bar" element={<FOHRoute><BarOrdersPage /></FOHRoute>} />
      <Route path="/orders" element={<FOHRoute><OrdersPage /></FOHRoute>} />
      <Route path="/orders/:orderId" element={<FOHRoute><TableOrderAt /></FOHRoute>} />
      <Route path="/kitchen" element={<FOHRoute><KitchenDisplayPage station="kitchen" /></FOHRoute>} />
      <Route path="/bar-board" element={<FOHRoute><KitchenDisplayPage station="bar" /></FOHRoute>} />
      {/* Verlo Service (auth plan item 6). The page itself checks `service.view` and
          shows a gentle "not available" state; the endpoint enforces it too. */}
      <Route path="/service" element={<FOHRoute><ServicePage /></FOHRoute>} />
      {/* The till (cash-off wave 1): the drawer, its movements, the cash-off and the Z read. */}
      <Route path="/till" element={<FOHRoute><TillPage /></FOHRoute>} />
      <Route path="/till/z/:sessionId" element={<FOHRoute><ZReadPage /></FOHRoute>} />
    </>
  );
}
